'use client';

import { c as _c } from "react/compiler-runtime";
import './selection-cards-group.scss';
import { useEffect, useId, useState } from 'react';
import { CheckboxGroupButtons, CheckboxGroupButtonsItem } from '@sola/ark-core/molecules/checkbox-group-buttons/checkbox-group-buttons.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { SelectionCard, SelectionCardProps, SelectionCardVariant } from '../selection-card/selection-card.tsx';
const computeCardId = (groupId: string, value: string) => `${groupId}-${value}`;
export type SelectionCardsGroupProps = {
  /** Array of selection cards */
  cards: SelectionCardProps[];
  /** Optional className to style the group container */
  className?: string;
  dynamicColumnLength?: boolean;
  /** Accessible label for the group */
  label?: string;
  /** Optional class name to style the list of cards */
  listClassName?: string;
  /** Allow a single items to be selected or multiple */
  selectionMode?: 'multiple' | 'single';
  /** Optional variant for the selection cards */
  variant?: SelectionCardVariant;
  /** Controlled value: array of selected values */
  value?: string[];
  /** Controlled change handler */
  onChange?: (nextValues: string[]) => void;
};
export const SelectionCardsGroup: React.FC<SelectionCardsGroupProps> = props => {
  const $ = _c(33);
  const {
    cards,
    label,
    className,
    dynamicColumnLength: t0,
    selectionMode,
    variant: t1,
    value,
    onChange
  } = props;
  const dynamicColumnLength = t0 === undefined ? false : t0;
  const variant = t1 === undefined ? "stacked" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [internalValue, setInternalValue] = useState(t2);
  let t3;
  if ($[1] !== value) {
    t3 = () => {
      if (value !== undefined) {
        setInternalValue(value);
      }
    };
    $[1] = value;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== selectionMode || $[4] !== value) {
    t4 = [selectionMode, value];
    $[3] = selectionMode;
    $[4] = value;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  useEffect(t3, t4);
  const isControlled = value !== undefined;
  const selectedValues = isControlled ? value : internalValue;
  let t5;
  if ($[6] !== isControlled || $[7] !== onChange) {
    t5 = nextValues => {
      if (onChange) {
        onChange(nextValues);
      }
      if (!isControlled) {
        setInternalValue(nextValues);
      }
    };
    $[6] = isControlled;
    $[7] = onChange;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleChange = t5;
  const groupId = useId();
  const lgGridCols = cards.length < 4 && dynamicColumnLength ? cards.length : 4;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      1: "lg:grid-cols-1",
      2: "lg:grid-cols-2",
      3: "lg:grid-cols-3",
      4: "lg:grid-cols-4"
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const gridColsLookup = t6 as const;
  const dynamicColClass = gridColsLookup[lgGridCols as keyof typeof gridColsLookup] || "lg:grid-cols-3";
  const t7 = `${"grid grid-cols-1 gap-snug-xl px-comfy-sm md:px-comfy-xl lg:px-0"} md:grid-cols-2 ${dynamicColClass} gap-comfy-sm md:gap-comfy-xl lg:gap-comfy-xs`;
  let t8;
  if ($[10] !== t7) {
    t8 = {
      stacked: t7,
      horizontal: "grid grid-cols-1 gap-snug-xl px-comfy-sm md:px-comfy-xl lg:px-0 lg:grid-cols-2 gap-comfy-sm lg:gap-comfy-xs "
    };
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const listClassesByVariant = t8 as const;
  let t9;
  if ($[12] !== className) {
    t9 = cn("SelectionCardsGroup", className);
    $[12] = className;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const t10 = listClassesByVariant[variant];
  let t11;
  if ($[14] !== cards || $[15] !== groupId || $[16] !== selectedValues || $[17] !== variant) {
    let t12;
    if ($[19] !== groupId || $[20] !== selectedValues || $[21] !== variant) {
      t12 = t13 => {
        const {
          ariaLabel,
          details,
          hideMediaWhenChecked: t14
        } = t13;
        const hideMediaWhenChecked = t14 === undefined ? false : t14;
        const {
          value: cardValue
        } = details;
        const checkboxId = computeCardId(groupId, cardValue);
        const isChecked = Array.isArray(selectedValues) ? selectedValues.includes(cardValue) : selectedValues === cardValue;
        return <CheckboxGroupButtonsItem id={checkboxId} value={cardValue} key={cardValue} ariaLabel={ariaLabel || details.label}><SelectionCard details={details} hideMediaWhenChecked={hideMediaWhenChecked} isChecked={isChecked} variant={variant} /></CheckboxGroupButtonsItem>;
      };
      $[19] = groupId;
      $[20] = selectedValues;
      $[21] = variant;
      $[22] = t12;
    } else {
      t12 = $[22];
    }
    t11 = cards.map(t12);
    $[14] = cards;
    $[15] = groupId;
    $[16] = selectedValues;
    $[17] = variant;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[23] !== handleChange || $[24] !== label || $[25] !== selectedValues || $[26] !== selectionMode || $[27] !== t10 || $[28] !== t11) {
    t12 = <CheckboxGroupButtons label={label} hideLabel={true} value={selectedValues} onChange={handleChange} selectionMode={selectionMode} listClassName={t10} className="pb-comfy-lg md:pb-0 lg:p-0">{t11}</CheckboxGroupButtons>;
    $[23] = handleChange;
    $[24] = label;
    $[25] = selectedValues;
    $[26] = selectionMode;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t12 || $[31] !== t9) {
    t13 = <div className={t9}>{t12}</div>;
    $[30] = t12;
    $[31] = t9;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  return t13;
};